import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { FiltruData } from '../../components/ui/FiltruData'

interface Document {
  id: number
  amazonInvoiceNumber: string | null
  docKind: 'factura' | 'nota_credit'
  originalInvoiceNumber: string | null
  eaDocType: string | null
  invoiceDate: string
  invoiceUrl: string | null
  amazonOrderId: string | null
  shipToCountry: string | null
  buyerName: string | null
  buyerCui: string | null
  buyerAddress: string | null
  buyerCity: string | null
  buyerPostcode: string | null
  currency: string
  vatRate: number
  bnrRate: number
  amountNet: number
  amountVat: number
  amountNetRon: number
  qty: number
  status: string
  eaError: string | null
  reintrareStoc: boolean
  probleme: string[]
  /** Din raportul de tranzactii (Amazon → Încasări). */
  incasare: { data: string; decontare: string; potrivire: string | null } | null
}

interface Raspuns {
  items: Document[]
  sumar: {
    documente: number
    facturi: number
    noteCredit: number
    bucati: number
    netEur: number
    tvaEur: number
    netRon: number
    tvaRon: number
    cuProbleme: number
  }
  perioade: string[]
  tari: string[]
}

const STATUS: Record<string, { label: string; cls: string }> = {
  imported: { label: 'Importată', cls: 'bg-slate-100 text-slate-700' },
  sent_ea: { label: 'Trimisă EA', cls: 'bg-green-100 text-green-700' },
  error_ea: { label: 'Eroare EA', cls: 'bg-red-100 text-red-700' },
}

const fmt = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const mesajEroare = (e: unknown) =>
  (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message ?? 'Eroare la import'

/**
 * Amazon — Facturi vânzare (VCS). Documentele emise de Amazon în numele firmei, importate din
 * raportul lunar „Amazon VAT Transactions", cu partenerul din fișierul de cumpărători.
 * Trimiterea în EA vine după factura de test (design 2026-10-24-amazon-facturi-vanzare).
 */
export function FacturiVanzareAmazonPage() {
  const qc = useQueryClient()
  const raportRef = useRef<HTMLInputElement>(null)
  const cumparatoriRef = useRef<HTMLInputElement>(null)
  const [de, setDe] = useState('')
  const [pana, setPana] = useState('')
  const [tara, setTara] = useState('')
  const [tip, setTip] = useState('')
  const [doarProbleme, setDoarProbleme] = useState(false)
  const [cautare, setCautare] = useState('')

  const { data, isLoading } = useQuery<Raspuns>({
    queryKey: ['amazon-vanzari', de, pana, tara, tip, doarProbleme, cautare],
    queryFn: async () => {
      const p = new URLSearchParams()
      if (de) p.set('from', de)
      if (pana) p.set('to', pana)
      if (tara) p.set('tara', tara)
      if (tip) p.set('tip', tip)
      if (doarProbleme) p.set('probleme', 'da')
      if (cautare.trim()) p.set('q', cautare.trim())
      return (await api.get(`/amazon/vanzari?${p}`)).data
    },
  })

  const reincarca = () => qc.invalidateQueries({ queryKey: ['amazon-vanzari'] })

  const importRaport = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return (await api.post('/amazon/vanzari/raport', fd)).data
    },
    onSuccess: (r) => {
      toast.success(
        `Raport importat: ${r.documente} documente (${r.noi} noi, ${r.actualizate} actualizate, ${r.neatinse} deja trimise). Sărite: ${r.sariteVine} Vine, ${r.sariteAlte} comisioane. Fără număr: ${r.faraNumar}.`,
      )
      reincarca()
    },
    onError: (e) => toast.error(mesajEroare(e)),
    onSettled: () => {
      if (raportRef.current) raportRef.current.value = ''
    },
  })

  const importCumparatori = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return (await api.post('/amazon/vanzari/cumparatori', fd)).data
    },
    onSuccess: (r) => {
      toast.success(
        `Cumpărători: ${r.randuri} rânduri, ${r.documenteActualizate} documente completate, ${r.comenziFaraDocument} comenzi fără document, ${r.faraNume} fără nume.`,
      )
      reincarca()
    },
    onError: (e) => toast.error(mesajEroare(e)),
    onSettled: () => {
      if (cumparatoriRef.current) cumparatoriRef.current.value = ''
    },
  })

  const seteazaNumar = useMutation({
    mutationFn: async ({ id, numar }: { id: number; numar: string }) =>
      (await api.patch(`/amazon/vanzari/${id}/numar`, { numar })).data,
    onSuccess: () => {
      toast.success('Număr salvat')
      reincarca()
    },
    onError: (e) => toast.error(mesajEroare(e)),
  })

  const trimiteUnul = useMutation({
    mutationFn: async (id: number) => (await api.post(`/amazon/vanzari/${id}/trimite-ea`)).data,
    onSuccess: (r: { stare: string; motive?: string[]; raspuns?: string }) => {
      if (r.stare === 'trimis') toast.success('Trimis în EA')
      else if (r.stare === 'blocat') toast.error(`Nu pleacă: ${r.motive?.join('; ')}`)
      else toast.error(`EA a respins: ${r.raspuns?.slice(0, 200)}`)
      reincarca()
    },
    onError: (e) => toast.error(mesajEroare(e)),
  })

  const trimiteToate = useMutation({
    mutationFn: async () => (await api.post('/amazon/vanzari/trimite-ea', {})).data,
    onSuccess: (r: { trimise: number; blocate: number; erori: number; oprit: string | null }) => {
      toast.success(
        `Trimise în EA: ${r.trimise}. Nu pleacă încă: ${r.blocate}. Erori: ${r.erori}.` +
          (r.oprit ? ` Oprit la ${r.oprit}` : ''),
      )
      reincarca()
    },
    onError: (e) => toast.error(mesajEroare(e)),
  })

  const reintrare = useMutation({
    mutationFn: async ({ id, v }: { id: number; v: boolean }) =>
      (await api.patch(`/amazon/vanzari/${id}/reintrare`, { reintrare: v })).data,
    onSuccess: reincarca,
    onError: (e) => toast.error(mesajEroare(e)),
  })

  const items = data?.items ?? []
  const s = data?.sumar

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Amazon — Facturi vânzare</h1>
          <p className="max-w-3xl text-sm text-slate-500">
            Facturile emise de Amazon în numele firmei (VCS), din raportul lunar „Amazon VAT
            Transactions”. Partenerul (numele și adresa cumpărătorului) vine din fișierul de
            cumpărători. „Trimite în EA” creează partenerul, dacă lipsește, și documentul de
            gestiune.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={raportRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) importRaport.mutate(f)
            }}
          />
          <input
            ref={cumparatoriRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) importCumparatori.mutate(f)
            }}
          />
          <button
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            disabled={importRaport.isPending}
            onClick={() => raportRef.current?.click()}
          >
            {importRaport.isPending ? 'Se încarcă…' : 'Încarcă raport TVA'}
          </button>
          <button
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
            disabled={importCumparatori.isPending}
            onClick={() => cumparatoriRef.current?.click()}
          >
            {importCumparatori.isPending ? 'Se încarcă…' : 'Încarcă cumpărători'}
          </button>
          <button
            className="rounded-md border border-green-600 px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-50 disabled:opacity-50"
            disabled={trimiteToate.isPending}
            title="Partenerul (dacă lipsește) și documentul de gestiune, pentru toate documentele care nu au probleme. Se oprește la prima respingere a EA."
            onClick={() => {
              if (window.confirm('Trimit în ExpertAccounts toate documentele gata de trimis?'))
                trimiteToate.mutate()
            }}
          >
            {trimiteToate.isPending ? 'Se trimite…' : 'Trimite în EA'}
          </button>
        </div>
      </div>

      {s && (
        <Card className="p-4">
          <div className="flex flex-wrap gap-6 text-sm">
            <Cifra eticheta="Documente" valoare={`${s.facturi} facturi + ${s.noteCredit} note`} />
            <Cifra eticheta="Bucăți" valoare={String(s.bucati)} />
            <Cifra eticheta="Fără TVA" valoare={`${fmt(s.netEur)} EUR`} />
            <Cifra eticheta="TVA" valoare={`${fmt(s.tvaEur)} EUR`} />
            <Cifra eticheta="Fără TVA, lei" valoare={`${fmt(s.netRon)} lei`} />
            <Cifra eticheta="TVA, lei" valoare={`${fmt(s.tvaRon)} lei`} />
            <Cifra
              eticheta="Cu probleme"
              valoare={String(s.cuProbleme)}
              cls={s.cuProbleme ? 'text-amber-600' : ''}
            />
          </div>
        </Card>
      )}

      <Card className="p-4">
        <FiltruData
          eticheta="Data facturii"
          de={de}
          pana={pana}
          onChange={(a, b) => (setDe(a), setPana(b))}
        />
      </Card>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <select
          className="rounded-md border px-2 py-1"
          value={tara}
          onChange={(e) => setTara(e.target.value)}
        >
          <option value="">Toate țările</option>
          {data?.tari.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          className="rounded-md border px-2 py-1"
          value={tip}
          onChange={(e) => setTip(e.target.value)}
        >
          <option value="">Facturi și note de credit</option>
          <option value="factura">Doar facturi</option>
          <option value="nota_credit">Doar note de credit</option>
        </select>
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={doarProbleme}
            onChange={(e) => setDoarProbleme(e.target.checked)}
          />
          Doar cu probleme
        </label>
        <input
          className="rounded-md border px-2 py-1"
          placeholder="Nr. factură, comandă, nume"
          value={cautare}
          onChange={(e) => setCautare(e.target.value)}
        />
      </div>

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-3 py-2">Nr. document</th>
              <th className="px-3 py-2">Dată</th>
              <th className="px-3 py-2">Comandă</th>
              <th className="px-3 py-2">Cumpărător</th>
              <th className="px-3 py-2">Țară</th>
              <th className="px-3 py-2">Tip EA</th>
              <th className="px-3 py-2 text-right">Buc.</th>
              <th className="px-3 py-2 text-right">Fără TVA</th>
              <th className="px-3 py-2 text-right">TVA</th>
              <th className="px-3 py-2 text-right">Curs</th>
              <th className="px-3 py-2 text-right">Lei fără TVA</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Încasare</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={13} className="px-3 py-6 text-center text-slate-400">
                  Se încarcă…
                </td>
              </tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={13} className="px-3 py-6 text-center text-slate-400">
                  Niciun document. Încarcă raportul „Amazon VAT Transactions” al lunii.
                </td>
              </tr>
            )}
            {items.map((d) => (
              <Rand
                key={d.id}
                d={d}
                onNumar={(numar) => seteazaNumar.mutate({ id: d.id, numar })}
                onTrimite={() => trimiteUnul.mutate(d.id)}
                onReintrare={(v) => reintrare.mutate({ id: d.id, v })}
              />
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

function Cifra({
  eticheta,
  valoare,
  cls = '',
}: {
  eticheta: string
  valoare: string
  cls?: string
}) {
  return (
    <div>
      <div className="text-slate-500">{eticheta}</div>
      <div className={`text-lg font-semibold ${cls}`}>{valoare}</div>
    </div>
  )
}

function Rand({
  d,
  onNumar,
  onTrimite,
  onReintrare,
}: {
  d: Document
  onNumar: (numar: string) => void
  onTrimite: () => void
  onReintrare: (v: boolean) => void
}) {
  const [numar, setNumar] = useState('')
  const st = STATUS[d.status] ?? { label: d.status, cls: 'bg-slate-100' }
  const trimis = d.status === 'sent_ea'
  return (
    <tr className="border-b align-top last:border-0 hover:bg-slate-50">
      <td className="px-3 py-2">
        {d.amazonInvoiceNumber ? (
          d.invoiceUrl ? (
            <a
              className="font-medium text-blue-700 hover:underline"
              href={d.invoiceUrl}
              target="_blank"
              rel="noreferrer"
              title="PDF-ul din Seller Central (cere autentificare)"
            >
              {d.amazonInvoiceNumber}
            </a>
          ) : (
            <span className="font-medium">{d.amazonInvoiceNumber}</span>
          )
        ) : trimis ? (
          '—'
        ) : (
          <form
            className="flex gap-1"
            onSubmit={(e) => {
              e.preventDefault()
              if (numar.trim()) onNumar(numar.trim())
            }}
          >
            <input
              className="w-36 rounded border px-1 py-0.5 text-xs"
              placeholder="Nr. din pagina comenzii"
              value={numar}
              onChange={(e) => setNumar(e.target.value)}
            />
            <button className="rounded bg-slate-200 px-2 text-xs" type="submit">
              Salvează
            </button>
          </form>
        )}
        {d.docKind === 'nota_credit' && (
          <div className="text-xs text-slate-500">
            notă de credit{d.originalInvoiceNumber ? ` la ${d.originalInvoiceNumber}` : ''}
            <label className="mt-1 flex items-center gap-1">
              <input
                type="checkbox"
                checked={d.reintrareStoc}
                disabled={trimis}
                onChange={(e) => onReintrare(e.target.checked)}
              />
              bucata s-a întors în stoc
            </label>
          </div>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2">{d.invoiceDate.slice(0, 10)}</td>
      <td className="px-3 py-2 text-xs text-slate-600">{d.amazonOrderId}</td>
      <td className="px-3 py-2">
        {d.buyerName ? (
          <>
            <div>{d.buyerName}</div>
            <div className="text-xs text-slate-500">
              {d.buyerCui} ·{' '}
              {[d.buyerAddress, d.buyerPostcode, d.buyerCity].filter(Boolean).join(', ')}
            </div>
          </>
        ) : (
          <span className="text-xs text-slate-400">
            {d.buyerPostcode} {d.buyerCity}
          </span>
        )}
      </td>
      <td className="px-3 py-2">{d.shipToCountry}</td>
      <td className="px-3 py-2 text-xs">{d.eaDocType ?? '—'}</td>
      <td className="px-3 py-2 text-right">{d.qty}</td>
      <td className="whitespace-nowrap px-3 py-2 text-right">
        {fmt(d.amountNet)} {d.currency}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right">
        {fmt(d.amountVat)} <span className="text-xs text-slate-400">({d.vatRate}%)</span>
      </td>
      <td className="px-3 py-2 text-right text-slate-500">{d.bnrRate || '—'}</td>
      <td className="px-3 py-2 text-right font-medium">{fmt(d.amountNetRon)}</td>
      <td className="px-3 py-2">
        <span className={`rounded-full px-2 py-0.5 text-xs ${st.cls}`}>{st.label}</span>
        {d.probleme.length > 0 && (
          <ul className="mt-1 list-disc pl-4 text-xs text-amber-700">
            {d.probleme.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
        {d.eaError && <div className="mt-1 text-xs text-red-600">{d.eaError}</div>}
        {!trimis && (
          <button className="mt-1 block text-xs text-green-700 hover:underline" onClick={onTrimite}>
            Trimite în EA
          </button>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-xs">
        {d.incasare ? (
          <span
            className={d.incasare.potrivire === 'potrivita' ? 'text-green-700' : 'text-amber-700'}
            title={`Decontarea ${d.incasare.decontare}${
              d.incasare.potrivire === 'potrivita' ? '' : ' — suma din raport diferă de document'
            }`}
          >
            {d.incasare.data.slice(8, 10)}.{d.incasare.data.slice(5, 7)}.
            {d.incasare.data.slice(0, 4)}
          </span>
        ) : (
          <span className="text-slate-400">neîncasată</span>
        )}
      </td>
    </tr>
  )
}
